/**
 * Game Over Screen - displays victory or defeat with statistics
 * Premium UI design with ornate decorations
 */

import { useGameStore, type GamePhase } from '@/state/gameStore';
import { FACTIONS } from '@/game/factions/FactionData';
import type { FactionId } from '@/types';

export class GameOverScreen {
  private container: HTMLElement;
  private unsubscribe: (() => void) | null = null;
  private onRestart?: () => void;
  private onSpectate?: () => void;
  private isSpectating: boolean = false;

  constructor() {
    this.container = document.createElement('div');
    this.container.id = 'game-over-screen';
    this.container.innerHTML = `
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Noto+Serif+SC:wght@400;700;900&display=swap');

        #game-over-screen {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 200;
          display: flex;
          align-items: center;
          justify-content: center;
          background: radial-gradient(ellipse at center, rgba(30, 20, 15, 0.92) 0%, rgba(10, 6, 4, 0.98) 100%);
          backdrop-filter: blur(12px);
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.6s ease-out;
          font-family: 'Noto Serif SC', -apple-system, BlinkMacSystemFont, 'Segoe UI', serif;
        }
        #game-over-screen.visible {
          opacity: 1;
          pointer-events: auto;
        }
        #game-over-screen.spectating {
          opacity: 0;
          pointer-events: none;
        }

        /* Main panel */
        .game-over-panel {
          position: relative;
          width: 420px;
          max-width: 90vw;
          background:
            linear-gradient(180deg, rgba(45, 35, 25, 0.95) 0%, rgba(25, 18, 12, 0.98) 100%),
            url('/images/ui/elements/wood.webp');
          background-size: cover, cover;
          background-blend-mode: overlay;
          border: 3px solid #8b6914;
          border-radius: 8px;
          box-shadow:
            0 0 80px rgba(0, 0, 0, 0.8),
            inset 0 0 60px rgba(0, 0, 0, 0.4),
            inset 0 2px 0 rgba(255, 220, 150, 0.1);
          padding: 50px 40px 40px;
          animation: panelAppear 0.5s ease-out;
        }
        @keyframes panelAppear {
          from { opacity: 0; transform: scale(0.9) translateY(20px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        .panel-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 100%;
        }

        /* Corner ornaments */
        .corner-ornament {
          position: absolute;
          width: 60px;
          height: 60px;
          background-image: url('/images/ui/decorations/corner-ornaments.png');
          background-size: 200% 200%;
          pointer-events: none;
          filter: drop-shadow(0 2px 4px rgba(0,0,0,0.5));
        }
        .corner-ornament.tl { top: -8px; left: -8px; background-position: 0% 0%; }
        .corner-ornament.tr { top: -8px; right: -8px; background-position: 100% 0%; }
        .corner-ornament.bl { bottom: -8px; left: -8px; background-position: 0% 100%; }
        .corner-ornament.br { bottom: -8px; right: -8px; background-position: 100% 100%; }

        /* Title */
        .game-over-title {
          font-size: 56px;
          font-weight: 900;
          text-align: center;
          margin-bottom: 8px;
          letter-spacing: 12px;
          text-indent: 12px;
        }
        .game-over-title.victory {
          color: #ffd35a;
          text-shadow:
            0 0 60px rgba(255, 180, 50, 0.8),
            0 0 30px rgba(255, 150, 30, 0.6),
            0 4px 8px rgba(0, 0, 0, 0.8);
        }
        .game-over-title.defeat {
          color: #e04040;
          text-shadow:
            0 0 60px rgba(220, 60, 60, 0.6),
            0 0 30px rgba(200, 40, 40, 0.5),
            0 4px 8px rgba(0, 0, 0, 0.8);
        }

        .game-over-subtitle {
          font-size: 16px;
          text-align: center;
          color: #a99878;
          margin-bottom: 30px;
          letter-spacing: 2px;
        }

        /* Faction display */
        .game-over-faction {
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: center;
          gap: 16px;
          margin-bottom: 28px;
          width: 100%;
        }
        .faction-emblem {
          width: 72px;
          height: 72px;
          min-width: 72px;
          min-height: 72px;
          border-radius: 50%;
          border: 3px solid #8b6914;
          box-shadow:
            0 0 20px rgba(0, 0, 0, 0.6),
            inset 0 0 15px rgba(0, 0, 0, 0.4);
          object-fit: cover;
          flex-shrink: 0;
        }
        .faction-name {
          font-size: 32px;
          font-weight: 700;
          color: #f5e6d3;
          text-shadow: 0 2px 6px rgba(0, 0, 0, 0.8);
        }

        /* Stats grid */
        .stats-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
          margin-bottom: 32px;
        }
        .stat-card {
          background:
            linear-gradient(145deg, rgba(60, 45, 30, 0.6) 0%, rgba(35, 25, 18, 0.8) 100%);
          border: 2px solid rgba(139, 105, 20, 0.5);
          border-radius: 6px;
          padding: 14px 18px;
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .stat-label {
          font-size: 13px;
          color: #a99878;
          white-space: nowrap;
        }
        .stat-value {
          font-size: 28px;
          font-weight: 700;
          color: #f5e6d3;
          margin-left: auto;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
        }

        /* Buttons */
        .game-over-buttons {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .go-btn {
          position: relative;
          width: 100%;
          padding: 16px 24px;
          border-radius: 4px;
          font-size: 18px;
          font-weight: 700;
          font-family: inherit;
          cursor: pointer;
          transition: all 0.2s ease;
          letter-spacing: 6px;
          text-indent: 6px;
          border: 2px solid;
          text-align: center;
        }
        .go-btn.primary {
          background: linear-gradient(180deg, #ffe17a 0%, #ffd35a 30%, #c9a227 70%, #a68520 100%);
          color: #3a2808;
          border-color: #8b6914;
          box-shadow:
            0 4px 20px rgba(255, 180, 50, 0.4),
            inset 0 2px 0 rgba(255, 255, 255, 0.4),
            inset 0 -2px 4px rgba(0, 0, 0, 0.2);
          text-shadow: 0 1px 0 rgba(255, 255, 255, 0.3);
        }
        .go-btn.primary:hover {
          transform: translateY(-2px);
          background: linear-gradient(180deg, #fff18a 0%, #ffe35a 30%, #d4b030 70%, #b89525 100%);
          box-shadow:
            0 8px 30px rgba(255, 180, 50, 0.5),
            inset 0 2px 0 rgba(255, 255, 255, 0.5),
            inset 0 -2px 4px rgba(0, 0, 0, 0.2);
        }
        .go-btn.spectate {
          background: linear-gradient(180deg, rgba(70, 100, 70, 0.9) 0%, rgba(45, 70, 45, 0.95) 100%);
          color: #c8e0c8;
          border-color: rgba(100, 140, 100, 0.7);
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.1);
        }
        .go-btn.spectate:hover {
          transform: translateY(-2px);
          background: linear-gradient(180deg, rgba(85, 120, 85, 0.95) 0%, rgba(55, 85, 55, 1) 100%);
          border-color: rgba(130, 180, 130, 0.8);
        }
        .go-btn.secondary {
          background: linear-gradient(180deg, rgba(55, 45, 35, 0.9) 0%, rgba(35, 28, 20, 0.95) 100%);
          color: #c9b896;
          border-color: rgba(139, 105, 20, 0.5);
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05);
        }
        .go-btn.secondary:hover {
          transform: translateY(-2px);
          background: linear-gradient(180deg, rgba(70, 55, 40, 0.95) 0%, rgba(45, 35, 25, 1) 100%);
          border-color: rgba(180, 140, 50, 0.6);
        }

        /* Spectating banner */
        .spectate-banner {
          position: fixed;
          top: 20px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 201;
          background: linear-gradient(145deg, rgba(35, 28, 20, 0.95) 0%, rgba(25, 18, 12, 0.98) 100%);
          padding: 14px 28px;
          border-radius: 6px;
          border: 2px solid rgba(139, 105, 20, 0.6);
          font-family: 'Noto Serif SC', serif;
          color: #c9b896;
          font-size: 14px;
          display: none;
          align-items: center;
          gap: 20px;
          box-shadow: 0 6px 30px rgba(0, 0, 0, 0.6);
        }
        .spectate-banner.visible {
          display: flex;
        }
        .spectate-exit-btn {
          background: linear-gradient(180deg, rgba(60, 50, 35, 0.9) 0%, rgba(40, 32, 22, 0.95) 100%);
          border: 1px solid rgba(139, 105, 20, 0.5);
          color: #e8e0d0;
          padding: 8px 18px;
          border-radius: 4px;
          cursor: pointer;
          font-family: inherit;
          font-size: 13px;
          transition: all 0.2s ease;
        }
        .spectate-exit-btn:hover {
          background: linear-gradient(180deg, rgba(80, 65, 45, 0.95) 0%, rgba(55, 45, 30, 1) 100%);
          border-color: rgba(180, 140, 50, 0.7);
        }
      </style>
      <div class="game-over-panel">
        <div class="corner-ornament tl"></div>
        <div class="corner-ornament tr"></div>
        <div class="corner-ornament bl"></div>
        <div class="corner-ornament br"></div>
        <div class="panel-content"></div>
      </div>
      <div class="spectate-banner" id="spectate-banner">
        <span>观战模式 - 你的王国已覆灭</span>
        <button class="spectate-exit-btn" id="exit-spectate-btn">退出观战</button>
      </div>
    `;

    document.body.appendChild(this.container);
    this.setupEventDelegation();
    this.setupSubscription();
  }

  setOnRestart(callback: () => void): void {
    this.onRestart = callback;
  }

  setOnSpectate(callback: () => void): void {
    this.onSpectate = callback;
  }

  private setupEventDelegation(): void {
    this.container.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;

      if (target.closest('#restart-btn')) {
        e.preventDefault();
        e.stopPropagation();
        this.hide();
        this.isSpectating = false;
        if (this.onRestart) {
          this.onRestart();
        }
        return;
      }

      if (target.closest('#menu-btn')) {
        e.preventDefault();
        e.stopPropagation();
        this.hide();
        this.isSpectating = false;
        useGameStore.getState().setPhase('menu');
        window.location.reload();
        return;
      }

      if (target.closest('#spectate-btn')) {
        e.preventDefault();
        e.stopPropagation();
        this.enterSpectateMode();
        return;
      }

      if (target.closest('#exit-spectate-btn')) {
        e.preventDefault();
        e.stopPropagation();
        this.exitSpectateMode();
        return;
      }
    });
  }

  private setupSubscription(): void {
    this.unsubscribe = useGameStore.subscribe((state) => {
      if (state.phase === 'victory' || state.phase === 'defeat') {
        if (!this.isSpectating) {
          this.show(state.phase, state.playerFaction, state.winner, state.gameTime);
        }
      } else {
        this.hide();
        this.isSpectating = false;
      }
    });
  }

  private show(
    phase: GamePhase,
    playerFaction: FactionId | null,
    _winner: FactionId | null,
    gameTime: number
  ): void {
    const content = this.container.querySelector('.panel-content');
    if (!content) return;

    const isVictory = phase === 'victory';
    const state = useGameStore.getState();
    const faction = playerFaction ? FACTIONS[playerFaction] : null;

    // Format time
    const minutes = Math.floor(gameTime / 60);
    const seconds = Math.floor(gameTime % 60);
    const timeStr = `${minutes}:${seconds.toString().padStart(2, '0')}`;

    content.innerHTML = `
      <div class="game-over-title ${isVictory ? 'victory' : 'defeat'}">
        ${isVictory ? '天下归一' : '战败'}
      </div>
      <div class="game-over-subtitle">
        ${isVictory ? '恭喜你一统天下！' : '你的王国已经覆灭...'}
      </div>

      ${faction ? `
        <div class="game-over-faction">
          <img class="faction-emblem" src="/images/emblems/${playerFaction}.webp" alt="${faction.name}" />
          <div class="faction-name">${faction.name}</div>
        </div>
      ` : ''}

      <div class="stats-grid">
        <div class="stat-card">
          <span class="stat-label">游戏时间</span>
          <span class="stat-value">${timeStr}</span>
        </div>
        <div class="stat-card">
          <span class="stat-label">单位数量</span>
          <span class="stat-value">${state.unitCount}</span>
        </div>
        <div class="stat-card">
          <span class="stat-label">领地数量</span>
          <span class="stat-value">${state.territoryCount}</span>
        </div>
        <div class="stat-card">
          <span class="stat-label">黄金</span>
          <span class="stat-value">${state.playerGold.toLocaleString()}</span>
        </div>
      </div>

      <div class="game-over-buttons">
        <button class="go-btn primary" id="restart-btn">再玩一局</button>
        ${!isVictory ? '<button class="go-btn spectate" id="spectate-btn">观战模式</button>' : ''}
        <button class="go-btn secondary" id="menu-btn">返回主菜单</button>
      </div>
    `;

    this.container.classList.add('visible');
    this.container.classList.remove('spectating');
  }

  private enterSpectateMode(): void {
    this.isSpectating = true;
    this.container.classList.add('spectating');

    const banner = this.container.querySelector('#spectate-banner');
    banner?.classList.add('visible');

    if (this.onSpectate) {
      this.onSpectate();
    }
  }

  private exitSpectateMode(): void {
    this.isSpectating = false;
    this.container.classList.remove('spectating');

    const banner = this.container.querySelector('#spectate-banner');
    banner?.classList.remove('visible');

    const state = useGameStore.getState();
    this.show(state.phase as GamePhase, state.playerFaction, state.winner, state.gameTime);
  }

  private hide(): void {
    this.container.classList.remove('visible');
    this.container.classList.remove('spectating');

    const banner = this.container.querySelector('#spectate-banner');
    banner?.classList.remove('visible');
  }

  dispose(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
    this.container.remove();
  }
}
